import { z } from 'zod';

import { isMac, isWindows } from '@/utils/capabilities';

import { SuggestionChipSchema } from './suggestion-chip.types';

export const ON_DEVICE_SELECTION = 'on-device';
export const SUGGESTED_SELECTION = 'suggested';

// Schema for connected account from backend
export const ConnectedAccountSchema = z.object({
  id: z.string(),
  toolkit_name: z.string(),
  display_name: z.string(),
  // .catch() so a stray/future status (e.g. a new backend state this client predates)
  // degrades that one account instead of rejecting the whole z.array() and white-screening
  // the Integrations page. Falls back to 'failed' — which renders a visible "Disconnected"
  // badge and stays reconnectable — NOT 'disconnected', which renders no badge at all (it's
  // intentionally omitted from the badge config) and would hide the degraded account.
  connection_status: z
    .enum([
      'pending',
      'connected',
      'failed',
      'expired',
      'disconnected',
      'cancelled',
    ])
    .catch('failed'),
  connected_at: z.string().nullable(),
  scopes: z.array(z.string()),
  connection_details: z.record(z.string(), z.any()),
  is_user_enabled: z.boolean(),
  account_label: z.string().default(''),
});

export type ConnectedAccount = z.infer<typeof ConnectedAccountSchema>;

/**
 * A connected account as it exists in the React Query cache
 */
export type CachedConnectedAccount = Omit<
  ConnectedAccount,
  'connection_status'
> & {
  connection_status: ConnectedAccount['connection_status'] | 'probing';
};

// Schema for toggle request
export const ToggleIntegrationSchema = z.object({
  toolkit_name: z.string(),
  user_enabled: z.boolean(),
});

export type ToggleIntegrationRequest = z.infer<typeof ToggleIntegrationSchema>;

export const OnDeviceMcpArgHintSchema = z.object({
  label: z.string(),
  kind: z.enum(['path', 'text']),
});

// Mirrors backend `OnDeviceEnvHint` (on_device_mcp_catalog.py). Pre-seeds one
// env-var row in the add-server form: `default` is a real (overridable) value,
// `placeholder` is display-only, `secret` masks the input.
export const OnDeviceEnvHintSchema = z.object({
  name: z.string(),
  default: z.string().nullable().optional(),
  placeholder: z.string().nullable().optional(),
  secret: z.boolean().default(false),
});

// Mirrors backend `ConfigRequirement` (on_device_mcp_catalog.py). Evaluated client-side
// when the user runs a connection test; `message` is rendered verbatim on failure.
export const ConfigRequirementSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('min_args'),
    min: z.number(),
    message: z.string(),
  }),
  z.object({ type: z.literal('env'), name: z.string(), message: z.string() }),
  z.object({
    type: z.literal('header'),
    name: z.string(),
    message: z.string(),
  }),
]);

export type ConfigRequirement = z.infer<typeof ConfigRequirementSchema>;

// Requirement kinds a newer backend may ship are dropped here, so the rest of
// the app only ever sees kinds it can evaluate and the catalog response never
// fails to parse over one.
export const ConfigRequirementsSchema = z
  .array(z.unknown())
  .default([])
  .transform((items) =>
    items.flatMap((item) => {
      const parsed = ConfigRequirementSchema.safeParse(item);
      return parsed.success ? [parsed.data] : [];
    })
  );

/** macOS permission the app itself must hold for a server to do its job. */
export const McpPermissionSchema = z.enum([
  'fullDiskAccess',
  'accessibility',
  'screenRecording',
]);

export type McpPermission = z.infer<typeof McpPermissionSchema>;

// Same forward-compat treatment as requirement kinds above: permissions a
// newer backend ships before this client supports them are dropped.
const McpPermissionsSchema = z
  .array(z.unknown())
  .default([])
  .transform((items) =>
    items.flatMap((item) => {
      const parsed = McpPermissionSchema.safeParse(item);
      return parsed.success ? [parsed.data] : [];
    })
  );

const IntegrationPlatformSchema = z.enum(['macos', 'windows']);

export type IntegrationPlatform = z.infer<typeof IntegrationPlatformSchema>;

const getDeviceIntegrationPlatform = (): IntegrationPlatform | null => {
  if (isMac()) return 'macos';
  if (isWindows()) return 'windows';
  return null;
};

/** No platform list means everywhere; a list means only those platforms */
export const supportedOnThisDevice = (
  platforms: readonly IntegrationPlatform[] | null | undefined
): boolean => {
  const platform = getDeviceIntegrationPlatform();
  return (
    !platforms?.length || (platform !== null && platforms.includes(platform))
  );
};

export const OnDeviceMcpCatalogEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  runtime: z.enum(['node', 'python']),
  package_spec: z.string(),
  arg_hints: z.array(OnDeviceMcpArgHintSchema).default([]),
  env_hints: z.array(OnDeviceEnvHintSchema).default([]),
  setup_instructions: z.array(z.string()).default([]),
  categories: z.array(z.string()).default([]),
  icon_url: z.string().nullable().optional(),
  requirements: ConfigRequirementsSchema,
  required_permissions: McpPermissionsSchema,
  platforms: z.array(IntegrationPlatformSchema).nullable().optional(),
});

export type OnDeviceMcpCatalogEntry = z.infer<
  typeof OnDeviceMcpCatalogEntrySchema
>;

// Schema for available integration info from backend
export const IntegrationInfoSchema = z.object({
  toolkit_name: z.string(),
  display_name: z.string(),
  description: z.string(),
  logo_url: z.string().nullable().optional(),
  is_internal: z.boolean().default(false),
  multi_account_enabled: z.boolean().default(false),
  categories: z.array(z.string()).default([]),
  featured: z.boolean().default(false),
  requires_paid_plan: z.boolean().default(true),
  on_device: z.boolean().default(false),
  platforms: z.array(IntegrationPlatformSchema).nullable().optional(),
});

// Available integration info (for UI display)
export interface IntegrationInfo {
  toolkit_name: string;
  display_name: string;
  description: string;
  logo_url?: string | null;
  is_internal?: boolean;
  multi_account_enabled?: boolean;
  categories?: string[];
  featured?: boolean;
  requires_paid_plan: boolean;
  on_device?: boolean;
  platforms?: IntegrationPlatform[] | null;
}

// Combined state for UI
export interface IntegrationState {
  info: IntegrationInfo;
  accounts: CachedConnectedAccount[];
  isConnected: boolean;
  isBeta?: boolean;
  betaTooltip?: string;
}

// Mirrors backend `IntegrationSuggestionResponse` (integrations_schemas.py)
export const IntegrationSuggestionSchema = z.object({
  chip: SuggestionChipSchema.nullable().optional().catch(null),
  toolkit_slug: z.string(),
  // `connected` only comes with `try` entries, which older clients never ask for
  source: z.enum([
    'usage',
    'chat',
    'routine',
    'onboarding',
    'connected',
    'reconnect',
  ]),
  reason: z.string(),
  // The three below default so a response from a backend that predates reconnects still parses
  // `try`: a just-connected toolkit, with the prompt to try in `reason`
  action: z.enum(['connect', 'reconnect', 'try']).default('connect'),
  // Composio account id or MCP server id, set for `reconnect`
  account_id: z.string().nullable().default(null),
  // Which account to reconnect, when the toolkit has several
  account_label: z.string().nullable().default(null),
  provider: z.enum(['composio', 'mcp', 'on_device']),
  platforms: z.array(IntegrationPlatformSchema).nullable().default(null),
  integration_id: z.string(),
  display_name: z.string(),
  icon_url: z.string().nullable(),
  mcp_url: z.string().nullable(),
  connect_target_id: z.string(),
  requires_paid_plan: z.boolean(),
});

export type IntegrationSuggestion = z.infer<typeof IntegrationSuggestionSchema>;

export type IntegrationTarget =
  | {
      kind: 'integration';
      toolkitName: string;
      displayName: string;
      requiresPaidPlan: boolean;
      // Re-authorizes this account instead of starting a new-account flow
      reconnectAccountId?: string;
    }
  | {
      kind: 'mcp';
      // Opens the add-server form for this catalog entry
      catalogId: string;
      displayName: string;
      url: string;
      requiresPaidPlan: boolean;
      reconnectServerId?: undefined;
    }
  | {
      kind: 'mcp';
      displayName: string;
      requiresPaidPlan: boolean;
      // Re-authorizes this server, catalog or custom, instead of opening the add-server form
      reconnectServerId: string;
    };

export type { EmailDraft } from '@littlebird/ui-utils';

export const ConnectionCardInfoSchema = z.object({
  provider: z.enum(['composio', 'mcp']),
  integration_id: z.string(),
  display_name: z.string(),
  description: z.string().default(''),
  action: z.enum(['connect', 'reconnect', 'enable']).default('connect'),
  state: z.enum(['pending', 'disabled', 'connected', 'unavailable']),
  redirect_url: z.string().url().nullable().default(null),
  account_id: z.string().default(''),
  server_id: z.string().default(''),
  flow_id: z.string().default(''),
});

export type ConnectionCardInfo = z.infer<typeof ConnectionCardInfoSchema>;

// Connect integration response schema
export const ConnectIntegrationResponseSchema = z.object({
  success: z.boolean(),
  redirect_url: z.string().nullable(),
  message: z.string(),
  account_id: z.string().nullable().optional(),
  // composio_connection_id of this flow; pass to cancel-oauth to abort it on popup close
  flow_id: z.string().nullable().optional(),
});

// Disconnect integration response schema
export const DisconnectIntegrationResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export type DisconnectIntegrationResponse = z.infer<
  typeof DisconnectIntegrationResponseSchema
>;
